import {
	ARTICLE_LIST,
	ARTICLELIST_ARTICLELIST,
	ARTICLELIST_STOREDLIST,
	AUTHENTICATION_KEY,
	DATA_REQUESTED,
	DATA_LOADED,
	DATA_UPDATED, ID_NOT_SET,
	GLOBAL_ARTICLELIST_KEY
} from "../../_MODULE_GLOBALS/constants";
import deepmerge from "deepmerge";
import {storeInCache} from "../../../utils/manageStoreCacheData";
import {getIntValue, isEmpty, isTrue} from "../../../utils/generalUtils";
import {findArticle} from "../../../utils/articleList/articleActions";
import {createNewStoreState} from "./generalReducerActions";

/*
   This reducer fetches and updates article list objects, which generally consist
   of an array of article objects.
 */

/**
 * Required store properties for a module that fetches lists of articles.
 *
 * This ensures that there is a placeholder in the store for
 * the values returned for the module.
 * Each action-type then initializes the state for it's store state.
 */
const createInitialState = ((storageKey) => {
	return {
		[storageKey]: {
			"collectionUrl": '',
			"articles": [],
			"articleId": '',
			"wdsArticleId": '',
			"articleList": [],
			"issueId": '',
			"issueUrl": '',
			"coverImageUrl": '',
			"section": '',
			"zoomLevel": '',

			"fetchListParams": {},
			"fetchQueryParams": null,
			"navigationAttributes": {},
			"fetchInProgress": false,

			"forceFetch": false,
			"replaceHistoryState": false,
			"updateHistoryState": true,

		}
	};
});
const createInitialShareState = ((storageKey, globalStoreKey) => {
	return {
		[storageKey]: {
			"collectionUrl": '',
			"fetchQueryParams": {},
			"articleList": [],
			"fetchInProgress": null
		},
		[globalStoreKey]: {
			"fetchQueryParams": {},
			"articleList": []
		}
	};
});
/**
 * List of attributes that should be re-initialized each time a reducer gets called.
 *
 * @type {function(*): {}}
 */
const resetStoreState = ((storageKey) => {
	return {
		[storageKey]: {
			"replaceHistoryState": false,
			"updateHistoryState": true,
			"forceStoreUpdate": false,
		}
	};
});



/**
 * Process returned article list from fetch and update the article that matches the
 * articlePreview object from the Article Editor.
 *
 * Use sessionStorage to keep content across reload in case of a browser major resize.
 *
 * Note: Just save preview to sessionStorage if articlePreview is not empty since
 * this won't happen very often.
 *
 * @param articleList article list from fetch
 * @param state store state
 * @returns {*}
 */
const updateArticleForEditorPreview = (articleList, state) => {
	const sessionPreview = window.sessionStorage.getItem("articleEditorPreview");
	const articlePreview = !isEmpty(state.articlePreview)
		? state.articlePreview
		: !isEmpty(sessionPreview) ? JSON.parse(sessionPreview) : {};
	if (!isEmpty(articlePreview)) {
		const articleId = getIntValue(articlePreview.articleId, ID_NOT_SET);
		const article = findArticle({articleList: articleList, articleId: articleId});
		if (!isEmpty(article)) {
			const articleIndex = articleList.findIndex(art => art.articleId === articleId);
			articleList[articleIndex] = Object.assign(article, articlePreview);
		}
		window.sessionStorage.setItem("articleEditorPreview", JSON.stringify(articlePreview));
	}
	return articleList;
};


/**
/**
 * Write ONLY the requested issue's access + accessPages (from the article-list response) into the
 * AUTHENTICATION store slice, which the authentication module and the replica path (getReplicaList)
 * read. Writing a single issue is deliberate: the auth slice's userAccess is a shared object that
 * the replica reducer and the globals SUBSCRIBER reducer also write per issue. If this writer
 * rewrote every issue in the response, it would zero out sibling issues that another viewer/writer
 * had just set, and the two writers would alternate on every render — the "flashing" between a
 * june-truth snapshot and a may-truth snapshot. Touching only the opened issue's key keeps each
 * writer confined to its own issue so the snapshots converge.
 *
 * access and accessPages are written faithfully and read together to tell the three cases apart,
 * since the server reports BOTH a truly owned issue AND a lookinside preview as access=true:
 *   owned            -> access=true,  accessPages=[]        (full access via login id)
 *   lookinside view  -> access=true,  accessPages=[1..5]    (page-limited preview via lookinside id)
 *   denied           -> access=false, accessPages=[]
 *
 * @param returnState the store state being built by the reducer
 * @param payloadUserAccess action.payload.userAccess from the article-list response
 * @param issueUrl the issueUrl this article-list call was scoped to
 */
const applyAccessPagesFromArticleList = (returnState, payloadUserAccess, issueUrl) => {
	if (isEmpty(issueUrl) || isEmpty(payloadUserAccess) || isEmpty(payloadUserAccess.accessPages)) {
		return;
	}
	const authStorageKey = AUTHENTICATION_KEY;
	if (!returnState[authStorageKey]) {
		returnState[authStorageKey] = {};
	}
	if (!returnState[authStorageKey].userAccess) {
		returnState[authStorageKey].userAccess = { isLoggedIn: true, access: {}, accessPages: {} };
	}
	const incomingAccess = payloadUserAccess.access || {};
	returnState[authStorageKey].userAccess = {
		...returnState[authStorageKey].userAccess,
		access: { ...returnState[authStorageKey].userAccess.access, [issueUrl]: isTrue(incomingAccess[issueUrl], {defaultValue: false}) },
		accessPages: { ...returnState[authStorageKey].userAccess.accessPages, [issueUrl]: payloadUserAccess.accessPages[issueUrl] || [] }
	};
};


function articleListReducers(state, action) {
	// check if module action call matches ARTICLE_LIST; if not present, set default to ARTICLE_LIST
	const moduleAction = (action.params && action.params.type) ? action.params.type : ARTICLE_LIST;

	if (moduleAction === ARTICLE_LIST && action.type === DATA_REQUESTED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ArticleList';
		const returnState = deepmerge(
			createInitialShareState(storageKey, GLOBAL_ARTICLELIST_KEY),
			state
		);
		returnState[storageKey].fetchInProgress = true;

		return returnState;
	}

	if (moduleAction === ARTICLE_LIST && action.type === DATA_LOADED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ArticleList';
		const returnState = deepmerge(
			createInitialShareState(storageKey, GLOBAL_ARTICLELIST_KEY),
			state
		);

		returnState[storageKey].collectionUrl = action.payload.collectionUrl ? action.payload.collectionUrl : '';
		const articleList = Array.isArray(action.payload.articles) ? updateArticleForEditorPreview(action.payload.articles, returnState) : [];
		const fetchQueryParams = action.params.fetchQueryParams ? action.params.fetchQueryParams : null;
		returnState[storageKey].articleList = articleList;
		returnState[storageKey].fetchQueryParams = fetchQueryParams;
		returnState[storageKey].docType = action.payload.docType ? action.payload.docType : '';

		// we need to store article lists for use by share (and maybe by multiple modules)
		// this property is read in PopulateGlobalModules and stored in the redux store
		returnState[GLOBAL_ARTICLELIST_KEY].articleList = articleList;
		returnState[GLOBAL_ARTICLELIST_KEY].fetchQueryParams = fetchQueryParams;

		// cache return articleList with query params as key
		returnState[storageKey].generatedCacheKey = storeInCache({dataKey: fetchQueryParams, keyType: "articles", data: articleList, overrideEmptyData: true, state: returnState});

		returnState[storageKey].fetchInProgress = false;

		// Propagate the requested issue's access + accessPages from the article-list response into
		// the AUTHENTICATION slice so the authentication module and the replica path (getReplicaList)
		// see this page-restricted (lookinside) issue as restricted / this owned issue as accessible.
		// Only issue-scoped article-list calls carry a queryParams.issueUrl; broad list calls
		// (featured/recent) have none and are skipped, so they can't clobber another issue's value.
		const requestedIssueUrl = (action.params && action.params.queryParams) ? action.params.queryParams.issueUrl : undefined;
		applyAccessPagesFromArticleList(returnState, action.payload.userAccess, requestedIssueUrl);

		return returnState;
	}

	if (moduleAction === ARTICLELIST_STOREDLIST && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ArticleList';
		const returnState = deepmerge(
			createInitialShareState(storageKey, GLOBAL_ARTICLELIST_KEY),
			state
		);

		const articleList = Array.isArray(action.payload.articleList) ? action.payload.articleList : [];
		const fetchQueryParams = action.params.fetchQueryParams ? action.params.fetchQueryParams : null;
		returnState[storageKey].articleList = articleList;
		returnState[storageKey].fetchQueryParams = fetchQueryParams;

		returnState[storageKey].fetchInProgress = false;

		return returnState;
	}

	// Action called through clicking on article from article list
	if (moduleAction === ARTICLELIST_ARTICLELIST && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ArticleList';

		// create new store state and update only changed attributes
		return createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});
	}


	return state;
}
export default articleListReducers;
